import React from 'react';
import { Link } from 'react-router-dom';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { ArrowRight, CheckCircle2, Flame, Lock, Settings, Radio } from 'lucide-react';
import CreatorGrowthEngine from '@/pages/new-app/CreatorGrowthEngine';

// Existing Consistency badge progress, reused as-is from the Rewards
// Program's "Consistency" badge category (badgeCategories → key: 'consistency').
const CONSISTENCY_BADGES = [
  { label: '3 Day Streak', state: 'earned' },
  { label: '7 Day Streak', state: 'in_progress', progress: 60 },
  { label: '30 Day Streak', state: 'locked' },
];
const BADGE_ICON = { earned: CheckCircle2, in_progress: Flame, locked: Lock };
const BADGE_STYLE = {
  earned: 'border-green-500/30 text-green-300 bg-green-500/10',
  in_progress: 'border-orange-500/30 text-orange-300 bg-orange-500/10',
  locked: 'border-white/10 text-[#555E6E]',
};

/** Milestones & Rewards tile dialog — brings together Personal Milestone
 * progress (reusing the same local state from Goals & Strategies), the
 * existing official Rewards Progress section (Creator Growth Engine,
 * "rewards" mode), and both posting + live-streaming streaks. Streak
 * figures reuse existing sample values already present elsewhere in the
 * app (Creator Growth Engine's momentum/strong-state streak copy, the
 * Rewards Program's Consistency badge progress, and the exact figures the
 * removed Live Analytics streak section used) — no new streak calculation
 * system is introduced. */
export default function MilestonesRewardsDialog({ open, onOpenChange, goals, milestones, onManageMilestones }) {
  const goalName = (id) => goals.find((g) => g.id === id)?.name || '—';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#14171F] border-white/10 text-white sm:max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="milestones-rewards-dialog">
        <DialogHeader><DialogTitle className="text-white">Milestones & Rewards</DialogTitle></DialogHeader>

        {/* Personal Milestones — explicitly distinct from official rewards */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <div>
              <p className="text-xs font-semibold text-white">Personal Milestone Progress</p>
              <p className="text-[10px] text-[#555E6E]">Your own tracking — separate from official Consistency Rewards below. No badges or points awarded here.</p>
            </div>
            <Button size="sm" variant="outline" className="border-white/10 shrink-0" onClick={onManageMilestones} data-testid="manage-milestones-btn">
              Manage<ArrowRight className="h-3.5 w-3.5 ml-1.5" />
            </Button>
          </div>
          <div className="space-y-2">
            {milestones.map((m) => {
              const pct = m.target ? Math.min(100, Math.round((m.current / m.target) * 100)) : 0;
              return (
                <Card key={m.id} className="bg-black/30 border-white/5" data-testid={`milestone-progress-${m.id}`}>
                  <CardContent className="p-3.5">
                    <p className="text-sm text-white">{m.name}</p>
                    <p className="text-[11px] text-[#8A93A6]">Goal: {goalName(m.goalId)} · {m.current}/{m.target} {m.unit}</p>
                    <Progress value={pct} className="h-1.5 bg-white/[0.04] [&>div]:bg-violet-400 mt-1.5" />
                  </CardContent>
                </Card>
              );
            })}
            {milestones.length === 0 && <p className="text-xs text-[#8A93A6] text-center py-4">No personal milestones yet.</p>}
          </div>
        </div>

        {/* Official Consistency Rewards — the existing Rewards Progress section, unchanged */}
        <div>
          <p className="text-xs font-semibold text-white mb-1">Official Consistency Rewards</p>
          <CreatorGrowthEngine mode="rewards" />
        </div>

        {/* Posting Streak */}
        <div>
          <p className="text-xs font-semibold text-white mb-2 flex items-center gap-2"><Flame className="h-3.5 w-3.5 text-orange-400" />Posting Streak</p>
          <Card className="glass border-white/5" data-testid="posting-streak-card">
            <CardContent className="p-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
                <div className="rounded-lg bg-white/5 border border-white/10 p-3 text-center">
                  <p className="text-2xl font-bold text-white">7</p>
                  <p className="text-[11px] text-[#8A93A6]">Current Streak (days)</p>
                </div>
                <div className="rounded-lg bg-white/5 border border-white/10 p-3 text-center">
                  <p className="text-2xl font-bold text-white">14</p>
                  <p className="text-[11px] text-[#8A93A6]">Longest Streak (days)</p>
                </div>
                <div className="rounded-lg bg-white/5 border border-white/10 p-3 text-center">
                  <p className="text-2xl font-bold text-white">12</p>
                  <p className="text-[11px] text-[#8A93A6]">Total Posts</p>
                </div>
              </div>
              <p className="text-[10px] text-[#555E6E] mb-2">Sample data · badge progress</p>
              <div className="flex flex-wrap gap-2 mb-3">
                {CONSISTENCY_BADGES.map((b) => {
                  const Icon = BADGE_ICON[b.state];
                  return (
                    <Badge key={b.label} variant="outline" className={`text-[10px] gap-1 ${BADGE_STYLE[b.state]}`} data-testid={`consistency-badge-${b.label.replace(/\s+/g, '-').toLowerCase()}`}>
                      <Icon className="h-3 w-3" />
                      {b.label}{b.state === 'in_progress' ? ` · ${b.progress}%` : ''}
                    </Badge>
                  );
                })}
              </div>
              <Link to="/app/account/settings" data-testid="streak-protection-settings-link">
                <Button size="sm" variant="outline" className="border-white/10 h-7 text-xs">
                  <Settings className="h-3.5 w-3.5 mr-1.5" />
                  Streak Protection Settings
                </Button>
              </Link>
            </CardContent>
          </Card>
        </div>

        {/* Live-Streaming Streak — relocated from Live Analytics, same values */}
        <div>
          <p className="text-xs font-semibold text-white mb-2 flex items-center gap-2"><Radio className="h-3.5 w-3.5 text-green-400" />Live-Streaming Streak</p>
          <Card className="glass border-white/5" data-testid="live-streak-card">
            <CardContent className="p-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div className="rounded-lg bg-white/5 border border-white/10 p-3 text-center">
                  <p className="text-2xl font-bold text-white">0</p>
                  <p className="text-[11px] text-[#8A93A6]">Current Streak (days)</p>
                </div>
                <div className="rounded-lg bg-white/5 border border-white/10 p-3 text-center">
                  <p className="text-2xl font-bold text-white">0</p>
                  <p className="text-[11px] text-[#8A93A6]">Best Streak (days)</p>
                </div>
                <div className="rounded-lg bg-white/5 border border-white/10 p-3 text-center">
                  <p className="text-2xl font-bold text-white">0</p>
                  <p className="text-[11px] text-[#8A93A6]">Total Lives This Month</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </DialogContent>
    </Dialog>
  );
}
